/**
 * @file 学びメモのペイン (リーダーの全画面ビューワの隣に出すメモ帳)。
 *
 * @what: いまの文脈 (資料・ページ) に合うノートを「ページ一致 → 資料一致 (別冊込み) →
 *   その他」の順で並べ、合うものがあれば先頭を自動で開く。編集は Markdown のテキスト
 *   エリアで、数秒おきに自動保存する。プレビュー切り替え・紐づけの編集 (いまのページ /
 *   この本 / ほかの本)・新規作成・削除 (確認ダイアログ) を持つ。
 *   画像は「画像を追加する」・貼り付け・ドロップで添付でき、本文へは `![](note-image:<id>)`
 *   の参照で差し込む (model/noteImages.ts)。プレビューと表示のときだけ blob URL に解決する。
 *   メモはまなぶ人 (リーダーで選んでいる人) のもので、一覧はその人のメモに絞る。新しい
 *   ノートはその人のものになり、まなぶ人が2人以上いれば紐づけの行で付け替えられる。
 * @why: 分割の軸 (本ごと・ページごと・日ごと・テーマごと) はデータで固定せず、ノート側の
 *   紐づけで表す (epic_23 §10・§11)。何も考えなければ「このページのメモ」と同じ操作感に
 *   なるよう、既定の動線 (自動で開く・1 タップ新規) を固くする。
 *   本文から参照が消えた画像も images には残す (書き直しで戻せる)。片づけはノート削除時。
 * @failure: 一覧の取得失敗は 1 行で知らせる。保存の失敗は書きかけを消さず、自動保存が
 *   次の変更でもう一度試す。画像の送信失敗は本文を変えない。削除の失敗も文言のみ
 *   (詳細は console)。
 *
 * 開いているメモの下書き・保存・画像は hooks/useStudyNoteEditor.ts が、編集欄の見た目は
 * StudyNoteEditor.tsx が持ち、学びメモの画面 (routes/StudyNotesRoute.tsx) と共有する。
 * このペインが持つのは、文脈に合うメモを開く処理・一覧・手書き・選んで聞く・外から渡す添付である。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAskRecords, type StudyAskRecordSummary } from "../api/askRecords";
import { AskRecordPanel } from "../guides/AskRecordPanel";
import { fetchStudyNotes, newStudyNoteId, type StudyNote, type StudyNoteRef } from "../api/notes";
import type { FamilyMember } from "../api/members";
import type { StudySource } from "../api/sources";
import { useNoteInk } from "../hooks/useNoteInk";
import {
  NOTE_IMAGES_MAX,
  NOTE_LIMIT_MESSAGE,
  useStudyNoteEditor,
} from "../hooks/useStudyNoteEditor";
import {
  PEN_COLORS,
  PEN_SIZES,
  isHandwritingImageId,
  type HandwritingTool,
} from "../model/handwriting";
import { HandwritingToolbar } from "./HandwritingToolbar";
import { InkCanvas } from "./InkCanvas";
import { summarizeNoteTags } from "../model/noteTags";
import { printedPageOf } from "../model/sourcePages";
import {
  contextSourceIds,
  noteContextRank,
  notesForLearner,
  sortNotesForContext,
} from "../model/studyNoteContext";
import { StudyDialog } from "./StudyDialog";
import { StudyNoteEditor } from "./StudyNoteEditor";
import { formatNoteDate, sameNoteRef, toggledNoteRefs } from "../model/noteList";
import {
  noteImageTarget,
  noteSectionTarget,
  noteTarget,
  type AskTarget,
} from "../model/askSelection";
import "./study.css";

type NotesState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; notes: StudyNote[] };

/** 一覧の行に出す、付いているものの数 (写真・手書き)。無いものは出さない。 */
function noteMeta(note: StudyNote): string[] {
  const images = note.images ?? [];
  const hw = images.filter((i) => isHandwritingImageId(i.id)).length;
  const photos = images.length - hw;
  const out: string[] = [];
  if (photos > 0) out.push(`画像${photos}`);
  if (hw > 0) out.push(`手書き${hw}`);
  return out;
}

export function StudyNotePane({
  familyId,
  source,
  sources,
  page,
  learnerPersonId,
  learners = [],
  view = "text",
  attachment = null,
  onAttached,
  onSelectTarget,
}: {
  familyId: string;
  /** いま開いている資料。 */
  source: StudySource;
  /** 本だなの全資料 (別冊の追随と、紐づけ先の名前解決に使う)。 */
  sources: readonly StudySource[];
  /** いま開いているページ (無ければ資料全体の文脈)。 */
  page?: number;
  /** リーダーで選んでいるまなぶ人である。一覧はこの人のメモに絞り、新しいノートはこの人のものになる。 */
  learnerPersonId: string | null;
  /** 家族のまなぶ人 (2人以上のとき、メモの付け替えの選択肢に出す)。 */
  learners?: readonly FamilyMember[];
  /** 見せ方である。text はテキストと画像、ink は同じメモの手書き。 */
  view?: "text" | "ink";
  /** 外から渡す添付 (ページと書き込みを重ねた1枚)。開いているメモに入れ、無ければ作って入れる。 */
  attachment?: { blob: Blob; name: string } | null;
  /** 添付を入れ終えたら呼ぶ。 */
  onAttached?: () => void;
  /** 「選んで聞く」の対象に足す (渡すと、メモ・節・画像を選べるようになる)。 */
  onSelectTarget?: ((target: AskTarget) => void) | undefined;
}) {
  const [notesState, setNotesState] = useState<NotesState>({ status: "loading" });
  // 「これを聞く」を押したあとの表示 (入れられた / 入れられないので本文を出す)。
  const [askHint, setAskHint] = useState<"copied" | "manual" | null>(null);
  const [listOpen, setListOpen] = useState(false);
  // このメモの AI 出力を、リーダーの上に重ねて開く (面を移らない)。
  const [asksOpen, setAsksOpen] = useState(false);

  const applySaved = useCallback((saved: StudyNote) => {
    setNotesState((prev) =>
      prev.status === "ready"
        ? {
            ...prev,
            notes: [saved, ...prev.notes.filter((n) => n.id !== saved.id)],
          }
        : prev,
    );
  }, []);
  const dropNote = useCallback((noteId: string) => {
    setNotesState((prev) =>
      prev.status === "ready" ? { ...prev, notes: prev.notes.filter((n) => n.id !== noteId) } : prev,
    );
    setListOpen(true);
  }, []);
  const editor = useStudyNoteEditor({
    familyId,
    learnerPersonId,
    onSaved: applySaved,
    onRemoved: dropNote,
    onDiscarded: dropNote,
  });
  const { selectedId, draft, dirty } = editor;

  // 「コピーしました」と AI 出力の対話は、押したメモの上だけのものにする。別のメモを開いたり
  // 一覧に戻ったら消す。
  useEffect(() => {
    setAskHint(null);
    setAsksOpen(false);
    setInkImageId(null);
  }, [selectedId, listOpen]);
  // 手書きのペイン (メモの添付の1種)。どの手書きを開いているかと道具を持つ。
  const [inkImageId, setInkImageId] = useState<string | null>(null);
  const [inkTool, setInkTool] = useState<HandwritingTool>("pen");
  const [inkColor, setInkColor] = useState(PEN_COLORS[0]!.id);
  const [inkSize, setInkSize] = useState(PEN_SIZES[1]!.id);

  useEffect(() => {
    const controller = new AbortController();
    fetchStudyNotes(familyId, controller.signal)
      .then((notes) => {
        if (!controller.signal.aborted) setNotesState({ status: "ready", notes });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load notes", err);
        setNotesState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId]);

  // まなぶ人の AI 出力の要約 (メモごとの件数を出すため)。読めなくてもペインは動く。
  const [askSummaries, setAskSummaries] = useState<readonly StudyAskRecordSummary[]>([]);
  useEffect(() => {
    if (learnerPersonId === null) {
      setAskSummaries([]);
      return;
    }
    const controller = new AbortController();
    fetchAskRecords(familyId, learnerPersonId, controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setAskSummaries(list);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load ask records for notes", err);
        setAskSummaries([]);
      });
    return () => controller.abort();
  }, [familyId, learnerPersonId]);

  // まなぶ人のメモに絞る (学習者を持たない古いメモは残す)。
  const notes = useMemo(
    () => (notesState.status === "ready" ? notesForLearner(notesState.notes, learnerPersonId) : []),
    [notesState, learnerPersonId],
  );
  const sourceIds = useMemo(() => contextSourceIds(sources, source.id), [sources, source.id]);
  const sorted = useMemo(
    () => sortNotesForContext(notes, sourceIds, page),
    [notes, sourceIds, page],
  );
  const titleBySource = useMemo(() => new Map(sources.map((s) => [s.id, s.title])), [sources]);
  const tagSuggestions = useMemo(() => summarizeNoteTags(notes).map((t) => t.tag), [notes]);

  const openNote = useCallback(
    (note: StudyNote) => {
      editor.openNote(note);
      setListOpen(false);
    },
    // editor.openNote は依存の変わらない関数である
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor.openNote],
  );

  // いま開いている文脈 (資料・ページ・まなぶ人)。これが変わったらノートを選び直す。
  const contextKey = `${source.id}:${page ?? ""}:${learnerPersonId ?? ""}`;
  // ページを除いた文脈である。これが同じなら、ページを送っただけと分かる。
  const bookKey = `${source.id}:${learnerPersonId ?? ""}`;
  const openedFor = useRef<string | null>(null);
  const openedBook = useRef<string | null>(null);
  const { save, draftToSave, closeNote } = editor;

  // 送った先のページに紐づいたノートがあれば、それを開く。無ければ、開いているノートのまま
  // にする。開いていなければ、資料に紐づいたノートを開くか、一覧 (新規の誘い) を出す。
  // ※なぜなら数ページにわたって1つのノートに書くとき、ページを送るたびに一覧へ戻ると
  //   書き続けられないためである。ページに紐づかないノートを開いている間は、見出しを
  //   「学びメモ」にして、このページのノートではないことを示す。
  useEffect(() => {
    if (notesState.status !== "ready") return;
    if (openedFor.current === contextKey) return;
    const pageTurnOnly = openedBook.current === bookKey;
    openedFor.current = contextKey;
    openedBook.current = bookKey;
    void (async () => {
      const first = sorted[0];
      const firstRank = first === undefined ? 2 : noteContextRank(first, sourceIds, page);
      const keepCurrent =
        pageTurnOnly &&
        selectedId !== null &&
        sorted.some((n) => n.id === selectedId) &&
        !(firstRank === 0 && first?.id !== selectedId);
      if (keepCurrent) return;
      // 書きかけがあれば、切り替える前に保存する。保存できなければ切り替えない。
      // ※なぜなら切り替えると書きかけが下書きごと消え、失敗の文言だけが別のノートの上に
      //   残るためである。この場合はそのノートに留まり、打鍵の再開で自動保存が再び試す。
      if (dirty && selectedId !== null && draft !== null) {
        const saved = await save(selectedId, draftToSave(draft));
        if (!saved) return;
      }
      const firstMatches =
        first !== undefined &&
        (first.refs ?? []).some(
          (r) =>
            r.kind === "source" &&
            sourceIds.has(r.source_id) &&
            (page === undefined || r.page === page || r.page === undefined),
        );
      if (first && firstMatches) {
        openNote(first);
        return;
      }
      closeNote();
      setAskHint(null);
      setListOpen(true);
    })();
  }, [
    notesState.status,
    contextKey,
    bookKey,
    learnerPersonId,
    sorted,
    sourceIds,
    page,
    openNote,
    dirty,
    selectedId,
    draft,
    save,
    draftToSave,
    closeNote,
  ]);

  const createNote = async () => {
    if (learnerPersonId === null) return;
    const id = newStudyNoteId();
    const refs: StudyNoteRef[] =
      page !== undefined
        ? [{ kind: "source", source_id: source.id, page }]
        : [{ kind: "source", source_id: source.id }];
    // 題に出す番号は紐づけの行 (refLabel) と同じく、紙に刷られた番号にする。
    const title =
      page !== undefined ? `p.${printedPageOf(source, page)} のメモ` : `${source.title} のメモ`;
    const note: StudyNote = {
      id,
      learner_person_id: learnerPersonId,
      title,
      refs,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    setListOpen(false);
    // 骨組みを先に保存しておく (書き始める前に閉じても一覧に残る)。
    await editor.createNote(note);
  };

  // 開いているメモの手書き (画像のうち hw- のもの)。ペインの切り替えタブに並べる。
  const handwritings = useMemo(
    () => (draft === null ? [] : draft.images.filter((i) => isHandwritingImageId(i.id))),
    [draft],
  );
  // inkImageId が "" のときは「新しい手書き」(まだ無い)。null は未指定で、最後の手書きを開く。
  const inkImage =
    inkImageId === ""
      ? null
      : (handwritings.find((i) => i.id === inkImageId) ??
        handwritings[handwritings.length - 1] ??
        null);
  const noteInk = useNoteInk({
    familyId,
    noteId: selectedId,
    image: inkImage,
    active: view === "ink",
    canCreate: draft !== null && draft.images.length < NOTE_IMAGES_MAX,
    onSaved: (saved) => {
      // 同じパスに上書きしたので、表示している絵を取り直す
      void editor.acquireImage(saved);
    },
    onCreate: (created) => {
      editor.appendCreatedImage(created);
      setInkImageId(created.id);
    },
  });

  // 外から渡された添付 (ページと書き込みを重ねた1枚) を、開いているメモに入れる。
  // メモが無ければ先に作る。同じ添付を二度入れないよう、入れたものを覚えておく。
  const attachedRef = useRef<{ blob: Blob; name: string } | null>(null);
  const createdForRef = useRef<{ blob: Blob; name: string } | null>(null);
  useEffect(() => {
    if (!attachment || attachedRef.current === attachment) return;
    if (notesState.status !== "ready") return;
    if (selectedId === null || draft === null) {
      // 文脈に合うメモは自動で開く (openedFor の効果)。開くものが無くて一覧になったときだけ作る。
      if (learnerPersonId !== null && listOpen && createdForRef.current !== attachment) {
        createdForRef.current = attachment;
        void createNote();
      }
      return;
    }
    attachedRef.current = attachment;
    void editor
      .attachImage(new File([attachment.blob], attachment.name, { type: attachment.blob.type }))
      .catch((err: unknown) => {
        console.error("[study] failed to attach a composed image", err);
        editor.setSaveError("ページの画像をメモに入れられませんでした。");
      })
      .finally(() => onAttached?.());
    // createNote / attachImage は最新の closure で呼ぶ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachment, notesState.status, selectedId, draft === null, listOpen]);

  // 紐づけ先を1行にする。保存しているのは連番なので、人へ見せるときに刷られた番号へ直す。
  const refLabel = (ref: StudyNoteRef): string => {
    const target = sources.find((s) => s.id === ref.source_id);
    const title = titleBySource.get(ref.source_id) ?? ref.source_id;
    if (ref.page === undefined || ref.page <= 0) return title;
    const printed = target ? printedPageOf(target, ref.page) : ref.page;
    return `${title} p.${printed}`;
  };

  const hasRef = (target: StudyNoteRef): boolean =>
    (draft?.refs ?? []).some((r) => sameNoteRef(r, target));
  const toggleRef = (target: StudyNoteRef) => {
    if (draft === null) return;
    editor.edit({ refs: toggledNoteRefs(draft.refs, target) });
  };

  const selectedNote = notes.find((n) => n.id === selectedId) ?? null;

  // 開いているメモを対象にした AI 出力の件数 (無ければ線を出さない)。
  const askCount = useMemo(
    () =>
      selectedId === null
        ? 0
        : askSummaries.filter((r) => r.targets.some((t) => t.note_id === selectedId)).length,
    [askSummaries, selectedId],
  );
  const allAsksHref = `/study/${encodeURIComponent(familyId)}/asks`;

  // 「選んで聞く」で選べるもの (このメモ全体・本文の節・添付の画像)。
  const [pickValue, setPickValue] = useState("note");
  const askPicks = useMemo(() => {
    if (selectedNote === null) return [];
    const out: { value: string; label: string; target: AskTarget }[] = [
      { value: "note", label: "このメモ", target: noteTarget(selectedNote) },
    ];
    const body = draft?.body ?? selectedNote.body ?? "";
    // 見出しは半角空白の後ろだけを拾う。※なぜなら CLI の節の切り出しは半角の空白しか見ず、
    //   全角空白の見出しは向こうで節として見つからないためである。
    const headings: string[] = [];
    for (const line of body.split("\n")) {
      const m = /^#{1,6} +(.+?) *$/.exec(line);
      if (m?.[1]) headings.push(m[1]);
    }
    // 同じ見出しや、他の見出しに含まれる見出しは出さない。※なぜなら CLI は見出しの部分一致で
    //   節を探し、当たるものが複数あると止まるためである。
    for (const heading of headings) {
      const ambiguous = headings.filter((h) => h.includes(heading)).length > 1;
      if (ambiguous) continue;
      out.push({
        value: `s:${heading}`,
        label: `節: ${heading}`,
        target: noteSectionTarget(selectedNote, heading),
      });
    }
    (selectedNote.images ?? []).forEach((img, i) => {
      out.push({
        value: `i:${img.id}`,
        label: `${i + 1}枚目の画像`,
        target: noteImageTarget(selectedNote, img.id, i + 1),
      });
    });
    return out;
  }, [selectedNote, draft?.body]);
  // メモを切り替えたときと、選んでいた節が本文から消えたときは、メモ全体に戻す。
  useEffect(() => {
    setPickValue("note");
  }, [selectedId]);
  useEffect(() => {
    if (askPicks.length > 0 && !askPicks.some((p) => p.value === pickValue)) setPickValue("note");
  }, [askPicks, pickValue]);

  // このノートについて AI 解説を求める呼び出しをクリップボードへ入れる。
  // ※なぜならメモの題名を思い出せないと、聞くたびに背景から説明することになるためである。
  // クリップボードが使えない場所 (安全でない接続など) では、代わりに本文を出す。
  const askCommand = selectedId !== null ? `/ai-explain note:${selectedId}` : null;
  const copyAskCommand = useCallback(async () => {
    if (askCommand === null) return;
    try {
      await navigator.clipboard.writeText(askCommand);
      setAskHint("copied");
    } catch {
      setAskHint("manual");
    }
  }, [askCommand]);
  const pageRef: StudyNoteRef | null =
    page !== undefined ? { kind: "source", source_id: source.id, page } : null;
  const wholeRef: StudyNoteRef = { kind: "source", source_id: source.id };

  return (
    <div className="study-note-pane">
      <div className="study-note-pane-head">
        <span className="study-note-pane-title">
          {listOpen || selectedNote === null
            ? "ノート"
            : `${noteContextRank(selectedNote, sourceIds, page) === 0 ? "このページの学びメモ" : "学びメモ"}${view === "ink" ? " (手書き)" : ""}`}
        </span>
        <span className="study-note-pane-actions">
          {!listOpen && selectedNote !== null ? (
            <>
              {onSelectTarget && view !== "ink" ? (
                <span className="study-note-pick">
                  <label className="study-note-pick-label">
                    <span>聞く対象</span>
                    <select
                      value={pickValue}
                      aria-label="聞く対象を選ぶ"
                      onChange={(e) => setPickValue(e.target.value)}
                    >
                      {askPicks.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="study-note-btn study-note-btn--ask"
                    onClick={() => {
                      const pick = askPicks.find((p) => p.value === pickValue) ?? askPicks[0];
                      if (pick) onSelectTarget(pick.target);
                    }}
                  >
                    <AskIcon />
                    選ぶ
                  </button>
                </span>
              ) : onSelectTarget ? null : (
                <button
                  type="button"
                  className="study-note-btn study-note-btn--ask"
                  onClick={() => void copyAskCommand()}
                >
                  <AskIcon />
                  AI 解説
                </button>
              )}
              {askCount > 0 ? (
                <button
                  type="button"
                  className="study-note-btn study-note-btn--outputs"
                  title="このメモについての AI 出力を読む"
                  aria-label={`AI 出力 ${askCount}件`}
                  onClick={() => setAsksOpen(true)}
                >
                  AI 出力
                  <span className="study-note-btn-count" aria-hidden="true">
                    {askCount}
                  </span>
                </button>
              ) : null}
              <button
                type="button"
                className="study-note-btn study-note-btn--quiet"
                onClick={() => setListOpen(true)}
              >
                <ListIcon />
                一覧
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="study-note-btn study-note-btn--primary"
            disabled={learnerPersonId === null}
            title={learnerPersonId === null ? "まなぶ人を選ぶとノートを作れます" : undefined}
            onClick={() => void createNote()}
          >
            <PlusIcon />
            新しいノート
          </button>
        </span>
      </div>

      {asksOpen && selectedId !== null && learnerPersonId !== null ? (
        <StudyDialog
          title={`メモ「${selectedNote?.title ?? ""}」の AI 出力`}
          labelledBy="study-note-asks-title"
          onClose={() => setAsksOpen(false)}
        >
          <AskRecordPanel
            familyId={familyId}
            personId={learnerPersonId}
            noteIds={[selectedId]}
            hideTitle
          />
          <p className="study-dialog-more">
            <Link to={allAsksHref}>ほかの AI 出力も見る</Link>
          </p>
        </StudyDialog>
      ) : null}

      {askHint !== null && askCommand !== null ? (
        <p className="study-note-muted" role="status">
          {askHint === "copied"
            ? `コピーしました。Claude Code に貼ると、このメモについて答えます: ${askCommand}`
            : `Claude Code にこれを貼ると、このメモについて答えます: ${askCommand}`}
        </p>
      ) : null}

      {editor.limitReached && (listOpen || selectedNote === null || draft === null) ? (
        <p role="alert" className="study-note-muted">
          {NOTE_LIMIT_MESSAGE}
        </p>
      ) : null}

      {notesState.status === "loading" ? (
        <p role="status" className="study-note-muted">
          ノートを読み込んでいます...
        </p>
      ) : notesState.status === "error" ? (
        <p role="alert" className="study-note-muted">
          ノートを読み込めませんでした。時間を置いて開き直してください。
        </p>
      ) : listOpen || selectedNote === null || draft === null ? (
        <div className="study-note-list" role="list" aria-label="ノートの一覧">
          {sorted.length === 0 ? (
            <p className="study-note-muted">
              まだノートがありません。「新しいノート」で、いま開いているところに紐づいた
              ノートを作れます。
            </p>
          ) : (
            sorted.map((n) => (
              <button
                key={n.id}
                type="button"
                role="listitem"
                className={
                  n.id === selectedId
                    ? "study-note-item study-note-item--active"
                    : "study-note-item"
                }
                onClick={() => openNote(n)}
              >
                <span className="study-note-item-title">{n.title}</span>
                <span className="study-note-item-refs">
                  {(n.refs ?? []).length === 0
                    ? "紐づけなし (自由ノート)"
                    : (n.refs ?? []).map(refLabel).join(" / ")}
                </span>
                <span className="study-note-item-meta">
                  {noteMeta(n).map((m) => (
                    <span key={m} className="study-note-item-badge">
                      {m}
                    </span>
                  ))}
                  <span>{formatNoteDate(n.updated_at)}</span>
                </span>
              </button>
            ))
          )}
        </div>
      ) : view === "ink" ? (
        <div className="study-note-ink">
          <div className="study-note-ink-tabs" role="group" aria-label="手書きの切り替え">
            {handwritings.map((img, i) => (
              <button
                key={img.id}
                type="button"
                className="study-note-ink-tab"
                aria-pressed={inkImage?.id === img.id}
                onClick={() => setInkImageId(img.id)}
              >
                {i + 1}枚目
              </button>
            ))}
            <button
              type="button"
              className="study-note-ink-tab"
              aria-pressed={inkImage === null}
              onClick={() => setInkImageId("")}
            >
              新しい手書き
            </button>
          </div>
          {inkImage === null ? (
            <p className="study-note-muted">白紙から書けます。書くと自動で保存します。</p>
          ) : null}
          <HandwritingToolbar
            tool={inkTool}
            colorId={inkColor}
            sizeId={inkSize}
            onTool={setInkTool}
            onColor={setInkColor}
            onSize={setInkSize}
            canUndo={noteInk.canUndo}
            canRedo={noteInk.canRedo}
            canClear={noteInk.canUndo}
            onUndo={noteInk.undo}
            onRedo={noteInk.redo}
            onClear={noteInk.clear}
          />
          <InkCanvas
            doc={noteInk.doc}
            onStroke={noteInk.push}
            tool={inkTool}
            colorId={inkColor}
            sizeId={inkSize}
            className="study-note-ink-canvas"
            ariaLabel="メモの手書き"
          />
          <p className="study-note-ink-state" role="status">
            {noteInk.status === "saving"
              ? "保存しています..."
              : noteInk.status === "error"
                ? "保存できませんでした"
                : noteInk.status === "full"
                  ? `画像はノートあたり${NOTE_IMAGES_MAX}枚までなので、新しい手書きは作れません`
                  : noteInk.status === "saved"
                    ? "保存しました"
                    : noteInk.status === "loading"
                      ? "読み込んでいます..."
                      : ""}
          </p>
        </div>
      ) : (
        <StudyNoteEditor
          editor={editor}
          learners={learners}
          learnerPersonId={learnerPersonId}
          sources={sources}
          refLabel={refLabel}
          tagSuggestions={tagSuggestions}
          refExtras={
            <>
              {pageRef !== null && !hasRef(pageRef) ? (
                <button
                  type="button"
                  className="study-note-chip-add"
                  onClick={() => toggleRef(pageRef)}
                >
                  ＋ いまのページ
                </button>
              ) : null}
              {!hasRef(wholeRef) ? (
                <button
                  type="button"
                  className="study-note-chip-add"
                  onClick={() => toggleRef(wholeRef)}
                >
                  ＋ この本
                </button>
              ) : null}
            </>
          }
        />
      )}
    </div>
  );
}

/**
 * ボタンの左に印を置き、役割を色と形の両方で見分けられるようにする。
 * ※なぜなら色だけの区別は、色の見分けに困る人と白黒の画面で失われるためである。
 */
function AskIcon() {
  return (
    <svg
      className="study-note-btn-icon"
      viewBox="0 0 14 14"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12.2 8.4a1.6 1.6 0 0 1-1.6 1.6H5.4L2.6 12V10a1.6 1.6 0 0 1-0.8-1.6V3.6A1.6 1.6 0 0 1 3.4 2h7.2a1.6 1.6 0 0 1 1.6 1.6z" />
      <path d="M5.9 5.2a1.2 1.2 0 0 1 2.3 0.4c0 0.8-1.1 1-1.1 1.7" />
      <path d="M7.1 8.6h0.01" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg
      className="study-note-btn-icon"
      viewBox="0 0 14 14"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    >
      <path d="M2.6 4h8.8M2.6 7h8.8M2.6 10h5.8" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      className="study-note-btn-icon"
      viewBox="0 0 14 14"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    >
      <path d="M7 2.8v8.4M2.8 7h8.4" />
    </svg>
  );
}
